const wnStickyProductCopy = window.WN_I18N?.product || {};

class WnProductStickyRail extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;
    this.ready = true;
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.productUrl = this.dataset.productUrl || '';
    this.productKey = this.normalizeProductUrl(this.productUrl);
    this.buttonLabel = this.dataset.buttonLabel || wnStickyProductCopy.add || 'Add';
    this.soldOutLabel = this.dataset.soldOutLabel || wnStickyProductCopy.notAvailable || 'Unavailable';
    this.rail = this.querySelector('[data-wn-nami-rail]');
    this.price = this.rail?.querySelector('[data-product-rail-price]');
    this.variantName = this.rail?.querySelector('[data-product-rail-variant]');
    this.image = this.rail?.querySelector('[data-product-rail-image]');
    this.button = this.rail?.querySelector('[data-product-rail-add]');
    this.variantData = this.rail?.querySelector('[data-product-rail-variants]');
    this.mainForm = [...document.querySelectorAll('wn-product-form')].find((form) => this.normalizeProductUrl(form.dataset.productUrl) === this.productKey)
      || document.querySelector('wn-product-form');
    this.productHtmlForm = this.mainForm?.querySelector('[data-type="product-form"]') || null;
    this.mainButton = this.productHtmlForm?.querySelector('[data-add-to-cart]') || null;
    this.purchaseState = null;
    this.currentVariant = null;

    try {
      this.variants = JSON.parse(this.variantData?.textContent || '[]');
    } catch {
      this.variants = [];
    }

    if (!this.rail || !this.productHtmlForm || !this.mainButton || !this.button) {
      this.restoreSafeState('missing-contract');
      return;
    }

    this.rail.hidden = false;
    this.rail.classList.remove('is-visible');
    this.rail.setAttribute('aria-hidden', 'true');
    this.rail.setAttribute('inert', '');

    this.wnNamiController = {
      productForm: this.mainForm,
      productHtmlForm: this.productHtmlForm,
      rail: this.rail,
      railAdd: this.button,
      railVariant: this.variantName,
      railPrice: this.price,
      abortController: this.abortController
    };

    this.button.addEventListener('click', (event) => {
      event.preventDefault();
      this.submitMainForm();
    }, { signal });

    document.addEventListener('wn:product:variant-change', (event) => this.onVariantChange(event), { signal });
    document.addEventListener('wn:nami:purchase-state-change', (event) => this.onPurchaseState(event), { signal });

    const initialId = this.mainForm?.querySelector('[data-variant-id]')?.value;
    const initialVariant = this.variants.find((item) => String(item.id) === String(initialId)) || this.variants[0];
    if (initialVariant) {
      this.currentVariant = initialVariant;
      this.renderVariantVisual(initialVariant);
      this.renderBase(initialVariant);
    }

    const stored = window.__WN_NAMI_PURCHASE_STATE__?.[this.productKey];
    if (stored) {
      this.purchaseState = stored;
      this.renderPurchase(stored);
    }

    this.dispatchEvent(new CustomEvent('wn:nami:ready', { bubbles: true }));
    requestAnimationFrame(() => this.localizeFooterCue());
  }

  disconnectedCallback() {
    this.abortController?.abort();
    delete this.wnNamiController;
    this.ready = false;
  }

  normalizeProductUrl(value) {
    try {
      return new URL(value || location.pathname, location.origin).pathname.replace(/\/$/, '') || '/';
    } catch {
      return String(value || location.pathname).replace(/\/$/, '') || '/';
    }
  }

  belongsToMainForm(event) {
    const source = event.target instanceof Element ? event.target.closest('wn-product-form') : null;
    return !source || !this.mainForm || source === this.mainForm;
  }

  submitMainForm() {
    if (!this.mainButton || !this.productHtmlForm || this.mainButton.disabled) {
      this.mainForm?.scrollIntoView({
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        block: 'center'
      });
      return;
    }
    this.productHtmlForm.requestSubmit();
  }

  onVariantChange(event) {
    if (!this.belongsToMainForm(event)) return;
    const variant = event.detail?.variant;
    if (!variant) return;
    const railVariant = this.variants.find((item) => String(item.id) === String(variant.id)) || variant;
    this.currentVariant = railVariant;
    this.renderVariantVisual(railVariant);
    const stored = window.__WN_NAMI_PURCHASE_STATE__?.[this.productKey];
    if (stored) {
      this.purchaseState = stored;
      this.renderPurchase(stored);
    } else if (this.purchaseState) {
      this.renderPurchase(this.purchaseState);
    } else {
      this.renderBase(railVariant);
    }
  }

  onPurchaseState(event) {
    const detail = event.detail || {};
    const eventKey = this.normalizeProductUrl(detail.productUrl || detail.state?.productUrl || '');
    if (eventKey && eventKey !== this.productKey) return;
    const state = detail.state || detail;
    if (!state || !state.mode) return;
    this.purchaseState = state;
    this.renderPurchase(state);
  }

  renderVariantVisual(variant) {
    if (this.image && variant.featured_image_url) {
      this.image.src = variant.featured_image_url;
      this.image.alt = variant.featured_image_alt || '';
    }
  }

  renderBase(variant) {
    if (this.price) this.price.textContent = variant.price_formatted || '';
    if (this.variantName) {
      const title = String(variant.title || '');
      const showTitle = title && title !== 'Default Title';
      this.variantName.hidden = !showTitle;
      this.variantName.textContent = showTitle ? title : '';
    }
    if (this.button) {
      this.button.disabled = !variant.available;
      this.button.textContent = variant.available ? this.buttonLabel : this.soldOutLabel;
    }
  }

  renderPurchase(state) {
    if (this.price) this.price.textContent = state.priceFormatted || '';
    if (this.variantName) {
      const label = String(state.displayLabel || state.label || '');
      this.variantName.hidden = !label;
      this.variantName.textContent = label;
    }
    if (this.button) {
      this.button.disabled = state.available === false;
      this.button.textContent = state.available === false ? this.soldOutLabel : this.buttonLabel;
    }
  }

  localizeFooterCue() {
    const label = this.dataset.wnStickyFooterLabel;
    if (!label) return;
    const cues = document.querySelectorAll('.wn-nami-footer-cue');
    const cue = cues[cues.length - 1];
    if (cue) cue.textContent = label;
  }

  restoreSafeState(reason = 'unknown') {
    this.mainButton?.classList.remove('wn-nami-source-hidden');
    if (this.rail) {
      this.rail.hidden = true;
      this.rail.classList.remove('is-visible', 'wn-nami-elastic-show', 'wn-nami-elastic-measure');
      this.rail.setAttribute('aria-hidden', 'true');
      this.rail.setAttribute('inert', '');
    }
    this.dataset.wnStickyAdapterFailed = reason;
  }
}

if (!customElements.get('wn-product-sticky-rail')) {
  customElements.define('wn-product-sticky-rail', WnProductStickyRail);
}
